import React, { FC, useCallback, useMemo } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { getIn } from 'final-form';
import classNames from 'classnames';
import validations from 'validations';
import RichTextEditorField from 'components/RichTextEditorField/RichTextEditorField.component';
import ClipIcon from 'icons/Clip.icon';
import { Button } from 'ncoded-component-library';
import useUploadMessageFile from 'hooks/useUploadMessageFile';
import { AxiosRequestConfig } from 'axios';
import UploadingFiles from 'components/MessageForm/components/UploadingFiles';
import { DropzoneFile } from 'components/Dropzone/utils';
import isEqual from 'lodash/isEqual';

import './RichTextEditorFieldWithFileUpload.styles.scss';

type RichTextEditorFieldWithFileUploadProps = {
  className?: string;
  label: string;
  richTextEditorName: string;
  filesName: string;
  initialFiles?: DropzoneFile[];
  onUploadRequest: (
    file: File,
    config: AxiosRequestConfig,
  ) => Promise<DropzoneFile>;
  onDeleteRequest: (fileId: string) => Promise<void>;
};

const RichTextEditorFieldWithFileUpload: FC<
  RichTextEditorFieldWithFileUploadProps
> = (props) => {
  const {
    className,
    label,
    richTextEditorName,
    filesName,
    initialFiles = [],
    onUploadRequest,
    onDeleteRequest,
  } = props;

  const classes = classNames(
    'rich-text-editor-field-with-file-upload',
    className,
  );

  const { mutators, change } = useForm();
  const { values } = useFormState();

  const formFiles = getIn(values, filesName) as DropzoneFile[];

  const onAddFile = useCallback(
    (file: DropzoneFile) => mutators.push(filesName, file),
    [filesName, mutators],
  );

  const onRemoveFile = useCallback(
    (fileId: string, file: DropzoneFile) => {
      if (initialFiles.find((el) => el._id === fileId)) onDeleteRequest(fileId);
      else
        change(
          filesName,
          formFiles?.filter((el) => !isEqual(el, file)),
        );
    },
    [filesName, formFiles, initialFiles, change, onDeleteRequest],
  );

  const {
    areAllFilesProcessed,
    files,
    restRootProps,
    getInputProps,
    onFileRemove,
    onFileUpload,
  } = useUploadMessageFile({
    initialValues: initialFiles,
    onUploadReq: onUploadRequest,
    onAddFile,
    onRemoveFile,
  });

  const additionalAction = useMemo(
    () => (
      <Button
        variant="icon"
        className="editor-action"
        icon={ClipIcon}
        onClick={onFileUpload}
      />
    ),
    [onFileUpload],
  );

  return (
    <>
      <div className={classes} {...restRootProps}>
        <input {...getInputProps()} />

        <RichTextEditorField
          label={label}
          name={richTextEditorName}
          maxLength={2000}
          validate={validations.richEditorRequired()}
          required
          additionalAction={additionalAction}
        >
          {!!files.length && (
            <UploadingFiles files={files} onRemove={onFileRemove} />
          )}
        </RichTextEditorField>
      </div>

      <Field
        name={filesName}
        key={String(areAllFilesProcessed)}
        validate={() => {
          if (!areAllFilesProcessed) {
            return true;
          }
        }}
      >
        {({ input }) => <input {...input} type="hidden" />}
      </Field>
    </>
  );
};

export default RichTextEditorFieldWithFileUpload;
